import {
  registerYouthRequestSchema,
  registerYouthResponseSchema as membersResponseSchema,
  type RegisterYouthResponse,
} from '@growup/accounts-shared';
import { registerYouthResponseSchema, registerYouthSchema } from '@growup/arena-shared';
import { Button, Card, DateInput, Field, Input, Notice, todayInLisbon } from '@growup/ui';
import { useState } from 'react';

import { messageOf, send } from '../api';

function isMinor(birthDate: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return false;
  const [y, m, d] = birthDate.split('-').map(Number) as [number, number, number];
  return Date.now() < Date.UTC(y + 18, m - 1, d);
}

/** "Maria João Silva" -> "maria.silva": a suggestion the monitor can change. */
export function suggestLogin(fullName: string): string {
  const parts = fullName
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  const last = parts.length > 1 ? `.${parts[parts.length - 1]!}` : '';
  return `${parts[0]!}${last}`.slice(0, 32);
}

const EMPTY = {
  fullName: '',
  birthDate: '',
  contactEmail: '',
  phone: '',
  ccNumber: '',
  street: '',
  postalCode: '',
  locality: '',
  guardianEmail: '',
  login: '',
  pin: '',
};

/**
 * Registration in the room, with the youth (RF-A2, flow G.4.2), in two steps:
 *
 * 1. members-api creates the account and keeps the personal data, encrypted.
 *    For a minor it emails the guardian an invitation to authorize.
 * 2. arena-api keeps the login and the PIN hash.
 *
 * If step 2 fails, retrying only repeats step 2 for the account already made.
 * After saving, the monitor only sees the login (RN-A16).
 */
export function RegisterYouthForm({
  arenaId,
  onDone,
}: {
  arenaId: string;
  onDone: (result: { accountId: string } | null) => void;
}) {
  const [form, setForm] = useState(EMPTY);
  const [loginTouched, setLoginTouched] = useState(false);
  const [created, setCreated] = useState<RegisterYouthResponse | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const minor = isMinor(form.birthDate);

  const set = (key: keyof typeof EMPTY) => (e: { target: { value: string } }) =>
    setForm((f) => {
      const next = { ...f, [key]: e.target.value };
      if (key === 'fullName' && !loginTouched) next.login = suggestLogin(e.target.value);
      return next;
    });
  const opt = (v: string) => (v.trim() === '' ? null : v.trim());
  const issues = (error: { issues: { path: (string | number)[]; message: string }[] }) =>
    error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(' · ');

  const savePin = async (account: RegisterYouthResponse) => {
    const body = registerYouthSchema.parse({
      accountId: account.accountId,
      login: account.login,
      pin: form.pin,
    });
    registerYouthResponseSchema.parse(
      await send('POST', `/api/arena/arenas/${arenaId}/youths`, body),
    );
    setDone(true);
  };

  const submit = async () => {
    setError(null);
    if (!/^\d{4}$/.test(form.pin)) {
      setError('O PIN tem 4 dígitos.');
      return;
    }
    setBusy(true);
    try {
      if (created) {
        await savePin(created);
        return;
      }
      const parsed = registerYouthRequestSchema.safeParse({
        login: form.login,
        arenaId,
        profile: {
          fullName: form.fullName,
          birthDate: form.birthDate,
          contactEmail: opt(form.contactEmail),
          phone: opt(form.phone),
          ccNumber: form.ccNumber,
          address: {
            street: form.street,
            postalCode: form.postalCode,
            locality: form.locality,
            country: 'PT',
          },
        },
        guardianEmail: minor ? opt(form.guardianEmail) : null,
      });
      if (!parsed.success) {
        setError(issues(parsed.error));
        return;
      }
      const account = membersResponseSchema.parse(
        await send('POST', '/api/members/youths', parsed.data),
      );
      setCreated(account);
      await savePin(account);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (done && created) {
    return (
      <Card headingLevel={2} title="Jovem registado">
        <p className="text-title">
          Login: <strong>{created.login}</strong>
        </p>
        {created.guardianInvite ? (
          <Notice tone="warning">
            O convite foi enviado para {created.guardianInvite.sentTo}. O jovem só desbloqueia PCs
            depois de o encarregado autorizar.
          </Notice>
        ) : (
          <Notice tone="success">Já pode desbloquear os PCs com o login e o PIN.</Notice>
        )}
        <div>
          <Button onClick={() => onDone({ accountId: created.accountId })}>Ver a ficha</Button>
        </div>
      </Card>
    );
  }

  const locked = created !== null;

  return (
    <Card headingLevel={2} title="Registar jovem">
      {locked ? (
        <Notice tone="warning">
          A conta {created.login} já foi criada. Falta só guardar o PIN na Arena.
        </Notice>
      ) : null}
      <fieldset disabled={locked} className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Nome completo">
          <Input value={form.fullName} onChange={set('fullName')} />
        </Field>
        <Field label="Data de nascimento">
          <DateInput
            value={form.birthDate}
            max={todayInLisbon()}
            onChange={(value) => set('birthDate')({ target: { value } })}
          />
        </Field>
        <Field label="N.º do Cartão de Cidadão">
          <Input value={form.ccNumber} onChange={set('ccNumber')} />
        </Field>
        <Field label={minor ? 'Email do jovem (opcional)' : 'Email'}>
          <Input type="email" value={form.contactEmail} onChange={set('contactEmail')} />
        </Field>
        <Field label={minor ? 'Telemóvel do jovem (opcional)' : 'Telemóvel'}>
          <Input value={form.phone} onChange={set('phone')} />
        </Field>
        <Field label="Morada">
          <Input value={form.street} onChange={set('street')} />
        </Field>
        <Field label="Código postal" hint="0000-000">
          <Input value={form.postalCode} onChange={set('postalCode')} />
        </Field>
        <Field label="Localidade">
          <Input value={form.locality} onChange={set('locality')} />
        </Field>
        {minor ? (
          <>
            <Notice tone="info" className="md:col-span-2">
              Menor de idade: o encarregado de educação recebe um convite por email e autoriza
              online.
            </Notice>
            <Field label="Email do encarregado">
              <Input type="email" value={form.guardianEmail} onChange={set('guardianEmail')} />
            </Field>
          </>
        ) : null}
        <Field label="Login" hint="É o nome que o jovem procura no PC.">
          <Input
            value={form.login}
            onChange={(e) => {
              setLoginTouched(true);
              set('login')(e);
            }}
          />
        </Field>
      </fieldset>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="PIN (4 dígitos)" hint="O jovem escreve-o sem o monitor ver.">
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={form.pin}
            onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, '') }))}
          />
        </Field>
      </div>
      <div className="flex flex-col gap-2">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="flex gap-2">
          <Button disabled={busy} onClick={() => void submit()}>
            {locked ? 'Guardar o PIN' : 'Registar'}
          </Button>
          <Button variant="secondary" onClick={() => onDone(null)}>
            Cancelar
          </Button>
        </div>
      </div>
    </Card>
  );
}
